import { useCallback, useEffect, useRef, useState } from "react";
import { AccessibilityInfo } from "react-native";
import { useTranslation } from "react-i18next";
import { releaseCapture, type ViewShotRef } from "react-native-view-shot";

import { reportShareError } from "../shareError";
import type { ReportPageNumber } from "./ReportPageHeader";
import type { ReportPageRun } from "./reportPagePlan";

// A page that never reports its layout (a test renderer) is captured anyway.
const LAYOUT_TIMEOUT_MS = 2000;
// After the layout, the charts measure themselves and draw once more.
// TODO(cleanup): a blind wait; no chart says «drawn» yet (wagmi draws on its own thread).
const SETTLE_MS = 150;

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// PG-64, inbox 978a9aae: the report's pages captured one after the other.
// ONE page is mounted offscreen at a time (`current`): mount, wait for its
// layout, capture, then the next. The URIs go to the host together, and
// their temporary files are dropped once the share is over (native only).
export function useReportPages(
  plan: () => ReportPageRun[],
  doShare: (uris: string[]) => Promise<void>,
  onError?: (message: string) => void,
) {
  const { t } = useTranslation();
  const cardRef = useRef<ViewShotRef>(null);
  const [current, setCurrent] = useState<ReportPageRun | null>(null);
  const [sharing, setSharing] = useState(false);
  const busy = useRef(false);
  const alive = useRef(true);
  const laidOut = useRef<(() => void) | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const onPageLayout = useCallback(() => laidOut.current?.(), []);

  const preparing = useCallback(
    ({ index, total }: ReportPageNumber) => {
      const page = t("common.pageOf", {
        current: index + 1,
        total,
        defaultValue: "{{current}} of {{total}}",
      });
      return t("mobile.progress.report.preparing", {
        page,
        defaultValue: "Preparing image {{page}}…",
      });
    },
    [t],
  );

  const onShare = useCallback(async () => {
    // Mounts one page, resolved by its layout.
    const show = (run: ReportPageRun) =>
      new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, LAYOUT_TIMEOUT_MS);
        laidOut.current = () => {
          clearTimeout(timer);
          resolve();
        };
        setCurrent(run);
      });
    if (busy.current) return;
    busy.current = true;
    setSharing(true);
    const uris: string[] = [];
    try {
      const runs = plan();
      if (runs[0]) AccessibilityInfo.announceForAccessibility(preparing(runs[0].page));
      for (const run of runs) {
        await show(run);
        await nextFrame();
        await nextFrame();
        await wait(SETTLE_MS);
        // The report closed while its pages were being drawn.
        if (!alive.current) return;
        const ref = cardRef.current;
        if (!ref || typeof ref.capture !== "function") {
          throw new Error("capture_not_ready");
        }
        uris.push(await ref.capture());
      }
      setCurrent(null);
      AccessibilityInfo.announceForAccessibility(
        t("mobile.progress.report.pagesReady", {
          defaultValue: "The images are ready to share.",
        }),
      );
      await doShare(uris);
    } catch (err) {
      if (alive.current) reportShareError(err, onError);
    } finally {
      // Shared, dismissed or failed: the files are no longer needed.
      uris.forEach(releaseCapture);
      laidOut.current = null;
      busy.current = false;
      if (alive.current) {
        setCurrent(null);
        setSharing(false);
      }
    }
  }, [plan, doShare, onError, preparing, t]);

  /** «Preparing image 3 of 9…» while the pages are drawn, else null. */
  const progress = current ? preparing(current.page) : null;

  return { cardRef, current, sharing, progress, onShare, onPageLayout };
}
